import {
  CREDIT_RATES,
  MAX_AI_TOOLS,
  MAX_AI_TOOL_INSTRUCTION_LENGTH,
  MAX_AI_TOOL_NAME_LENGTH,
  countWords,
  isBuiltInTool,
  type AiTool,
  type AiToolInput,
} from '@lyrmo/shared';
import { useEffect, useId, useState } from 'react';
import { Button } from '../../components/Button';
import type { Messages } from '../../i18n/en';
import { useI18n } from '../../i18n/i18n-context';
import { toolItems, type MenuItem } from '../rewrite/menu-items';
import { useAccount } from './account-context';
import { AccountFrame } from './AccountFrame';
import {
  ConfirmDelete,
  DuplicateBuiltIn,
  ItemHead,
  ItemSection,
  ItemSettings,
  ManagerLayout,
  ManagerList,
  OwnActions,
  TrySample,
} from './ItemManager';
import { useTrials } from './useTrials';
// Its form is laid out like the tones and instructions dialogs', with their styles.
import './tones.css';

/** What's wrong with the tool, in the writer's terms, before the server says so. */
function problemWith(
  { name, instruction }: AiToolInput,
  others: readonly AiTool[],
  t: Messages,
): string | null {
  if (!name) return t.aiTools.nameMissing;
  if (others.some((other) => other.name.toLowerCase() === name.toLowerCase())) {
    return t.aiTools.nameTaken(name);
  }
  if (!instruction) return t.aiTools.instructionMissing;
  return null;
}

interface ToolFormProps {
  /** The tool to change, or null to make a new one. */
  readonly tool: AiTool | null;
  /** What the form starts with: the tool's own, or a duplicate's. */
  readonly draft: AiToolInput;
  /** The writer's other tools, whose names this one can't take. */
  readonly others: readonly AiTool[];
  readonly onSaved: (tool: AiTool) => void;
  readonly onCancel: () => void;
}

function ToolForm({ tool, draft: initial, others, onSaved, onCancel }: ToolFormProps) {
  const { t, describeError } = useI18n();
  const { client } = useAccount();
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ids = { name: useId(), instruction: useId() };

  const update = (changes: Partial<AiToolInput>) => {
    setDraft({ ...draft, ...changes });
    setError(null);
  };

  async function save() {
    const input = { name: draft.name.trim(), instruction: draft.instruction.trim() };
    const problem = problemWith(input, others, t);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    try {
      onSaved(tool ? await client.updateAiTool(tool.id, input) : await client.createAiTool(input));
    } catch (caught) {
      setError(describeError(caught));
      setSaving(false);
    }
  }

  return (
    <form
      className="tone-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <h3 className="item-name">{tool ? t.menus.editItem(tool.name) : t.aiTools.newTool}</h3>
      <div className="tone-field">
        <label className="field-label" htmlFor={ids.name}>
          {t.aiTools.name}
        </label>
        <input
          id={ids.name}
          className="text-input"
          required
          maxLength={MAX_AI_TOOL_NAME_LENGTH}
          placeholder={t.aiTools.namePlaceholder}
          value={draft.name}
          onChange={(event) => {
            update({ name: event.target.value });
          }}
        />
      </div>

      <div className="tone-field">
        <label className="field-label" htmlFor={ids.instruction}>
          {t.aiTools.instruction}
        </label>
        <textarea
          id={ids.instruction}
          className="text-input tone-notes"
          rows={5}
          required
          maxLength={MAX_AI_TOOL_INSTRUCTION_LENGTH}
          placeholder={t.aiTools.instructionPlaceholder}
          value={draft.instruction}
          onChange={(event) => {
            update({ instruction: event.target.value });
          }}
        />
        <p className="field-hint">
          {draft.instruction.length} / {MAX_AI_TOOL_INSTRUCTION_LENGTH}
        </p>
      </div>

      {error && (
        <p className="dialog-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <Button onClick={onCancel}>{t.common.cancel}</Button>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? t.aiTools.saving : t.aiTools.save}
        </Button>
      </div>
    </form>
  );
}

/** What the right of the dialog shows: a tool, or the form making or changing one. */
type Pane =
  | { readonly kind: 'tool'; readonly id: string }
  | {
      readonly kind: 'form';
      readonly tool: AiTool | null;
      readonly draft: AiToolInput;
      /** The tool to show again on Cancel; null when made from the menu, closing instead. */
      readonly back: string | null;
    };

const FIRST_TOOL = 'bullets';

/**
 * Every AI tool the writer has, the built-in ones and their own: whether the menu shows each,
 * whether it's pinned, what it does, and a try of it on a sample. Their own tools are changed,
 * duplicated, and deleted here; built-in ones are duplicated to make one that works differently.
 */
function AiToolsManager() {
  const { t, format, describeError } = useI18n();
  const {
    client,
    aiTools,
    reloadAiTools,
    menuSettings,
    reloadMenuSettings,
    keepMenuItem,
    newAiTool,
    closeDialog,
  } = useAccount();
  // Opened from the AI tools menu's New tool, it starts with a new one.
  const [pane, setPane] = useState<Pane>(() =>
    newAiTool
      ? { kind: 'form', tool: null, draft: { name: '', instruction: '' }, back: null }
      : { kind: 'tool', id: FIRST_TOOL },
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { trials, tryIt, forget } = useTrials();
  const own = aiTools ?? [];
  const settings = menuSettings.tools;
  const items = toolItems(own, settings, t);
  const full = aiTools !== null && aiTools.length >= MAX_AI_TOOLS;

  // Opening the dialog brings the tools up to date, as they can change in another tab.
  useEffect(() => {
    Promise.all([reloadAiTools(), reloadMenuSettings('tools')]).catch((caught: unknown) => {
      setLoadError(describeError(caught));
    });
  }, [describeError, reloadAiTools, reloadMenuSettings]);

  const show = (id: string) => {
    setPane({ kind: 'tool', id });
    setSaved(null);
    setDeleting(false);
    setError(null);
  };

  const edit = (tool: AiTool | null, draft: AiToolInput, back: string | null) => {
    setPane({ kind: 'form', tool, draft, back });
    setSaved(null);
  };

  const duplicate = (item: MenuItem) => {
    const instruction = isBuiltInTool(item.id) ? t.aiTools.about[item.id] : item.hint;
    const name = t.menus.copyName(item.name).slice(0, MAX_AI_TOOL_NAME_LENGTH);
    edit(null, { name, instruction }, item.id);
  };

  async function remove(tool: AiTool) {
    setBusy(true);
    setError(null);
    try {
      await client.deleteAiTool(tool.id);
      await Promise.all([reloadAiTools(), reloadMenuSettings('tools')]);
      show(FIRST_TOOL);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  const selected = pane.kind === 'tool' ? pane.id : (pane.tool?.id ?? null);

  const details = () => {
    if (pane.kind === 'form') {
      return (
        <ToolForm
          // A fresh form for each tool, or duplicate, it's opened for.
          key={`${pane.tool?.id ?? 'new'}:${pane.draft.name}`}
          tool={pane.tool}
          draft={pane.draft}
          others={own.filter((other) => other.id !== pane.tool?.id)}
          onSaved={(tool) => {
            // Its try, if any, was of what it did before.
            forget(tool.id);
            // Shown once it's listed, so the list and the tool come up together.
            const done = () => {
              show(tool.id);
              setSaved(tool.name);
            };
            reloadAiTools().then(done, (caught: unknown) => {
              done();
              setError(describeError(caught));
            });
          }}
          onCancel={() => {
            // Made from the menu, it goes back to the draft.
            if (pane.back === null && newAiTool && !pane.tool) closeDialog();
            else show(pane.tool?.id ?? pane.back ?? FIRST_TOOL);
          }}
        />
      );
    }

    // Deleted meanwhile, as in another tab, the first tool takes its place.
    const item = items.find((candidate) => candidate.id === pane.id) ?? items[0];
    if (!item) return null;
    const tool = own.find((candidate) => candidate.id === item.id);
    const credits = countWords(t.menus.sample) * CREDIT_RATES.toolPerWord;
    return (
      <div className="item-details" key={item.id}>
        {saved && (
          <p className="tones-saved" role="status">
            {t.aiTools.saved(saved)}
          </p>
        )}
        <ItemHead
          item={item}
          actions={
            tool ? (
              <OwnActions
                name={tool.name}
                full={full}
                busy={busy}
                onEdit={() => {
                  edit(tool, tool, tool.id);
                }}
                onDuplicate={() => {
                  duplicate(item);
                }}
                onDelete={() => {
                  setDeleting(true);
                }}
              />
            ) : (
              <DuplicateBuiltIn
                disabled={full || aiTools === null}
                onClick={() => {
                  duplicate(item);
                }}
              />
            )
          }
        />
        {tool && deleting && (
          <ConfirmDelete
            message={t.aiTools.confirmDelete(tool.name)}
            busy={busy}
            onCancel={() => {
              setDeleting(false);
            }}
            onConfirm={() => void remove(tool)}
          />
        )}
        {full && <p className="field-hint">{t.aiTools.limit(MAX_AI_TOOLS)}</p>}

        <ItemSettings
          item={item}
          ready={settings !== null}
          onKeep={(change) => {
            setError(null);
            keepMenuItem('tools', {
              id: item.id,
              hidden: item.hidden,
              pinned: item.pinned,
              ...change,
            }).catch((caught: unknown) => {
              setError(describeError(caught));
            });
          }}
        />
        {error && (
          <p className="dialog-error" role="alert">
            {error}
          </p>
        )}

        <ItemSection title={t.aiTools.whatItDoes}>
          {isBuiltInTool(item.id) ? (
            <>
              <p className="item-about">{t.aiTools.about[item.id]}</p>
              <p className="item-note">{t.menus.builtInNote}</p>
            </>
          ) : (
            <p className="item-text">{item.hint}</p>
          )}
        </ItemSection>

        <TrySample
          trial={trials[item.id]}
          cost={`${format.number(credits)}${t.account.chipUnit(credits)}`}
          onTry={() =>
            void tryIt(item.id, (signal) => client.runTool(t.menus.sample, item.id, { signal }))
          }
        />
      </div>
    );
  };

  return (
    <ManagerLayout
      shown={items.filter((item) => !item.hidden).length}
      total={items.length}
      list={
        <ManagerList
          groups={[
            { label: t.menus.builtIn, items: items.filter((item) => item.builtIn) },
            {
              label: t.aiTools.yours,
              items: items.filter((item) => !item.builtIn),
              aside: aiTools && t.menus.ownCount(aiTools.length, MAX_AI_TOOLS),
              empty:
                aiTools === null ? (
                  loadError ? (
                    <p className="dialog-error" role="alert">
                      {loadError}
                    </p>
                  ) : (
                    <p className="manager-empty" role="status">
                      {t.aiTools.loading}
                    </p>
                  )
                ) : (
                  <p className="manager-empty">{t.aiTools.empty}</p>
                ),
            },
          ]}
          selected={selected}
          onSelect={show}
          newLabel={t.aiTools.newTool}
          newDisabled={aiTools === null || full}
          onNew={() => {
            edit(null, { name: '', instruction: '' }, selected);
          }}
        />
      }
      details={details()}
    />
  );
}

export function AiToolsDialog() {
  const { t } = useI18n();
  const { account, dialog, closeDialog, openDialog } = useAccount();
  const signedIn = Boolean(account?.user);
  return (
    <AccountFrame
      open={dialog === 'ai-tools'}
      onClose={closeDialog}
      title={t.aiTools.title}
      wide={signedIn}
    >
      {signedIn ? (
        <AiToolsManager />
      ) : (
        <>
          <p className="dialog-intro">{t.aiTools.pitch}</p>
          <div className="form-actions">
            <Button onClick={closeDialog}>{t.common.notNow}</Button>
            <Button
              variant="primary"
              onClick={() => {
                openDialog('sign-in');
              }}
            >
              {t.common.signIn}
            </Button>
          </div>
        </>
      )}
    </AccountFrame>
  );
}
